import { createClient } from "graphql-ws";
import { logger } from "@artis/logger";
import { print } from "graphql";
let clientIdCounter = 0;
const wsStats = { connections: 0, subscriptions: 0 };
/** Returns a snapshot of module-level WebSocket stats (across all clients). */
export const getWsStats = () => ({ ...wsStats });
const toGetter = (h) => (typeof h === "function" ? h : async () => h);
export const createGraphqlClient = (url, headers) => {
    const getHeaders = toGetter(headers);
    const clientId = ++clientIdCounter;
    let wsClient = null;
    let onWsClosed = null;
    logger.debug(`GraphQL client created`, { tags: ["gql"], clientId });
    return {
        execute: createQuery(url, getHeaders),
        withHeaders: (extraHeaders) => ({
            execute: createQuery(url, async () => ({ ...(await getHeaders()), ...extraHeaders })),
        }),
        subscribe: (query, ...args) => {
            if (!wsClient) {
                wsClient = createGraphqlWsClient(clientId, url, getHeaders, () => onWsClosed?.());
            }
            const cb = args[1] ? args[1] : args[0];
            const v = args[1] ? args[0] : {};
            return subscribe(clientId, wsClient, query, cb, v);
        },
        dispose: async () => {
            logger.info(`GraphQL client disposed`, { tags: ["gql"], clientId, ...wsStats });
            if (wsClient) {
                const closed = new Promise((resolve) => {
                    onWsClosed = resolve;
                });
                try {
                    await wsClient.dispose();
                    await closed;
                    logger.info(`GraphQL client disposed`, { tags: ["gql"], clientId, ...wsStats });
                }
                catch (err) {
                    logger.error("Failed to dispose GraphQL WS client", err, { tags: ["gql"], clientId });
                }
            }
        },
    };
};
function subscribe(clientId, client, document, callbacks, variables) {
    const query = print(document).replace(/^(\s*)query\s+/m, "$1subscription ");
    const name = /.+?\s+([A-Za-z0-9_]+)/.exec(query)?.[1] ?? "unknown";
    wsStats.subscriptions++;
    const span = logger.span("debug", `GraphQL subscription ${name}`, {
        tags: ["gql"],
        query,
        variables,
        clientId,
        subscriptions: wsStats.subscriptions,
    });
    // biome-ignore lint/suspicious/noExplicitAny: CBA to find the correct type
    const unsub = client.subscribe({
        query: query,
        variables,
    }, {
        next: (x) => {
            span.exit();
            callbacks.next?.(x);
        },
        error: (err) => {
            span.exit();
            logger.error("GraphQL subscription error", err, { tags: ["gql"], query, variables });
            callbacks.error?.(err);
        },
        complete: callbacks.complete ?? (() => { }),
    });
    return () => {
        unsub();
        wsStats.subscriptions--;
        logger.debug(`GraphQL subscription ${name} unsubscribed`, {
            tags: ["gql"],
            clientId,
            subscriptions: wsStats.subscriptions,
        });
    };
}
const RETRY_BACKOFF_CAP_MS = 16_000;
const RETRY_JITTER_RATIO = 0.5;
const createGraphqlWsClient = (clientId, url, getHeaders, onClose) => {
    return createClient({
        url: url.replace("https", "wss").replace("http", "ws"),
        connectionParams: async () => ({ headers: await getHeaders() }),
        shouldRetry: (errOrCloseEvent) => {
            const e = errOrCloseEvent;
            logger.debug(`GraphQL websocket shouldRetry?`, {
                tags: ["gql"],
                clientId,
                code: e.code,
                message: e.message,
                type: e.type,
            });
            return true;
        },
        retryAttempts: Infinity,
        retryWait: async (retries) => {
            const base = Math.min(1000 * 2 ** retries, RETRY_BACKOFF_CAP_MS);
            const delay = base + Math.random() * base * RETRY_JITTER_RATIO;
            logger.info(`GraphQL websocket retryWait`, { tags: ["gql"], clientId, attempt: retries, delay });
            await new Promise((resolve) => setTimeout(resolve, delay));
        },
        keepAlive: 10000,
        on: wsEventHandlers(clientId, onClose),
    });
};
export const extractWsErrorInfo = (error) => {
    if (error instanceof Error)
        return error;
    const e = error;
    const code = e.code ?? e.status;
    const reason = e.reason || e.message || e.type || "unknown";
    return new Error(`WebSocket error: code=${String(code ?? "none")}, reason=${String(reason)}`);
};
const WS_OPEN = 1;
// 3x the 10s keepAlive: a healthy socket resets this via inbound pongs well before it fires, tolerating ~2 missed pongs.
const ACTIVITY_TIMEOUT_MS = 30_000;
// 4408 (Request Timeout) is a retryable close code, so closing with it triggers the reconnect path.
const RECONNECT_CLOSE_CODE = 4408;
export const wsEventHandlers = (_clientId, onClose) => {
    let activeSocket = null;
    let activityTimer = null;
    const clearActivityTimer = () => {
        if (activityTimer)
            clearTimeout(activityTimer);
        activityTimer = null;
    };
    const bumpActivity = () => {
        clearActivityTimer();
        activityTimer = setTimeout(() => {
            activityTimer = null;
            if (activeSocket?.readyState === WS_OPEN) {
                logger.warn("GraphQL websocket idle timeout; no inbound activity in 30s, closing to force reconnect", {
                    tags: ["gql"],
                });
                activeSocket.close(RECONNECT_CLOSE_CODE, "activity timeout");
            }
        }, ACTIVITY_TIMEOUT_MS);
    };
    return {
        connected: (socket) => {
            activeSocket = socket;
            wsStats.connections++;
            logger.info("GraphQL websocket connected", { tags: ["gql"] });
            bumpActivity();
        },
        message: () => bumpActivity(),
        pong: (received) => {
            if (received)
                bumpActivity();
        },
        closed: (event) => {
            clearActivityTimer();
            wsStats.connections--;
            const e = event;
            const info = { code: e.code, reason: e.reason, wasClean: e.wasClean };
            if (e.code === 1000) {
                logger.info("GraphQL websocket closed", { tags: ["gql"], info });
            }
            else {
                logger.warn("GraphQL websocket closed abnormally", { tags: ["gql"], info });
            }
            onClose?.();
        },
        error: (error) => {
            logger.error("GraphQL websocket error", extractWsErrorInfo(error), { tags: ["gql"] });
        },
    };
};
const createQuery = (url, getHeaders) => async (queryDoc, ...variables) => {
    const query = print(queryDoc).replace(/^(\s*)subscription\s+/m, "$1query ");
    const name = /.+?\s+([A-Za-z0-9_]+)/.exec(query)?.[1] ?? "unknown";
    const span = logger.span("debug", `GraphQL query ${name}`, { tags: ["gql"], query, variables: variables[0] });
    const response = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/graphql-response+json",
            ...(await getHeaders()),
        },
        body: JSON.stringify({ query: query, variables: variables[0] }),
    });
    if (!response.ok) {
        span.exit();
        const text = await response.text().catch(() => "unknown error");
        logger.error("GraphQL query network error", { tags: ["gql"], query, variables: variables[0], text });
        throw new Error("Network response was not ok");
    }
    span.exit();
    // biome-ignore lint/suspicious/noExplicitAny: Its fine
    const json = (await response.json());
    if (typeof json.data !== "undefined")
        return json;
    // Hasura's own message is usually top-level `.message`, but in dev-mode/internal errors
    // (e.g. an unhandled Postgres exception) the useful detail can instead live nested under
    // `.extensions.internal.error.message` (or `.extensions.internal.message`) with a generic
    // top-level message like "Internal Server Error" - check those too before giving up.
    const messages = Array.isArray(json.errors)
        ? json.errors
            // biome-ignore lint/suspicious/noExplicitAny: shape of a GraphQL error response
            .map((e) => e?.extensions?.internal?.error?.message ?? e?.extensions?.internal?.message ?? e?.message)
            .filter((m) => typeof m === "string" && m.length > 0)
        : [];
    // Keep the raw Hasura message(s) as the error message (no extra prefix) so existing
    // callers matching on message prefixes (e.g. "Uniqueness violation") keep working, and
    // fall back to the raw errors payload (or the printed query as a last resort) only if
    // Hasura didn't give us any usable message at all.
    const message = messages.length > 0
        ? messages.join("; ")
        : Array.isArray(json.errors) && json.errors.length > 0
            ? `Failed query (no error message): ${JSON.stringify(json.errors)}`
            : `Failed query: ${print(queryDoc)}`;
    const error = new Error(message);
    Object.assign(error, json);
    // Stringify the response explicitly - logger/console object inspection truncates nested
    // objects (e.g. `errors: [ [Object] ]`), which has previously hidden the real Hasura error.
    logger.error("Failed", error, {
        tags: ["gql"],
        query: queryDoc,
        variables: variables[0],
        response: JSON.stringify(json),
    });
    return Promise.reject(error);
};
//# sourceMappingURL=client.js.map